/**
 * O vídeo da webcam de cada pessoa na conferência (BRAZ-20), para mostrar ao vivo no lugar da foto.
 * A chamada vive no RoomDesktop (useVoiceChat); este contexto leva os streams até a lista de
 * Participantes.
 *
 * Só quem está na chamada recebe o vídeo dos outros: a malha liga apenas os participantes. Quem
 * está fora, e as janelas próprias (que têm a própria conexão, sem a chamada), continuam com a foto.
 */
import { createContext, useContext } from 'react';
import type { RtcMedia } from '../shared.js';
import type { VoiceChat } from './useVoiceChat.js';

/** O vídeo a mostrar no lugar da foto de alguém, ou null. */
export type VideoOf = (userId: string) => MediaStream | null;

export const VideoContext = createContext<VideoOf>(() => null);

export const useVideoOf = (): VideoOf => useContext(VideoContext);

const hasVideo = (stream: MediaStream | null | undefined): stream is MediaStream =>
  !!stream && stream.getVideoTracks().some((track) => track.enabled && track.readyState === 'live');

/**
 * Quem aparece em vídeo: eu, na chamada e com a câmera ligada; os outros, quando publicam vídeo
 * (`peerMedia`) e o stream deles já chegou com uma faixa de vídeo.
 */
export function videoOf(
  voice: Pick<VoiceChat, 'inCall' | 'camOn' | 'localStream' | 'remoteStreams'>,
  peerMedia: Record<string, RtcMedia | undefined>,
  meId: string | undefined,
): VideoOf {
  return (userId) => {
    if (!voice.inCall) return null;
    if (userId === meId) return voice.camOn && hasVideo(voice.localStream) ? voice.localStream : null;
    const stream = voice.remoteStreams[userId];
    return peerMedia[userId]?.video && hasVideo(stream) ? stream : null;
  };
}
